import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import * as _ from 'underscore';

import { bindActionCreators } from 'redux';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { ContentFilterKeyName, ContentFiltersKeys } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { SessionSiteSelectors, SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SiteTasksSubspaceName, siteTasksSubspaceNameToExplorableItemType, SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedSiteTasksSpace } from './ConnectedSiteTasksSpace';
import { SiteTasksSpaceOwnProps } from './SiteTasksSpace';

export interface SiteTasksSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly subspaceName: SiteTasksSubspaceName;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: SiteTasksSpaceOwnProps) => JSX.Element;
}

export interface SiteTasksSpaceProviderStateProps {}

export interface SiteTasksSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
}

interface SiteTasksSpaceProviderPropsFromAngular {
  readonly telemetry: Telemetry;
}

export type SiteTasksSpaceNonAngularProps = SiteTasksSpaceProviderOwnProps & SiteTasksSpaceProviderStateProps & SiteTasksSpaceProviderDispatchProps;

export type SiteTasksSpaceProviderContainerProps = SiteTasksSpaceNonAngularProps & SiteTasksSpaceProviderPropsFromAngular;

export interface SiteTasksSpaceProviderState {}

export class SiteTasksSpaceProviderContainer extends React.PureComponent<SiteTasksSpaceProviderContainerProps, SiteTasksSpaceProviderState> {

  public constructor(props: SiteTasksSpaceProviderContainerProps) {
    super(props);
  }

  public render() {
    return this.renderSpace();
  }

  private renderSpace = () => {
    const props: SiteTasksSpaceOwnProps = {
      angularContext: this.props.angularContext,
      telemetry: this.props.telemetry,
      urls: this.props.urls,
    };
    return this.props.renderSpace(props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: SiteTasksSpaceProviderContainerProps) {
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  public componentWillUnmount() {}

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'sitetasks',
        subspaceName: this.props.subspaceName
      }
    );
    // We are just switching tabs so we know content counts are up to date.
    this.props.contentCountsUpToDate();

    this.props.requestSpaceContents();
  }
}

export namespace SiteTasksSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
    };
  }

  function selectPropsFromAngularServices(angularServices: SiteTasksSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<SiteTasksSpaceNonAngularProps, SiteTasksSpaceProviderPropsFromAngular, SiteTasksSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const SiteTasksSpaceProviderAng: React.ComponentType<SiteTasksSpaceNonAngularProps> = SiteTasksSpaceProviderAngularProvider.HOC(SiteTasksSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<SiteTasksSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  contentCountsUpToDate,
}, dispatch);

export const ConnectedSiteTasksSpaceProviderContainer: React.ComponentClass<SiteTasksSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(SiteTasksSpaceProviderAng);

const renderSpace = (props: SiteTasksSpaceOwnProps): JSX.Element => <ConnectedSiteTasksSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type SiteTasksSpaceProviderProps = LocationPathProp & Omit<SiteTasksSpaceProviderOwnProps, 'renderSpace'>;

export function SiteTasksSpaceProvider(props: SiteTasksSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: true,
    shouldUpdateContentTypes: false,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'sitetasks',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedSiteTasksSpaceProviderContainer {...{...props, renderSpace}} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}
